// src/features/feedback/components/VoteStatisticsDialog.jsx
import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  LinearProgress,
  Chip,
} from '@mui/material';
import {
  Close,
  BarChart,
  Business,
  People,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useFeedbackVoteStatistics } from '../hooks/useFeedbackVote';

export default function VoteStatisticsDialog({ open, onClose, feedbackId, feedbackTitle }) {
  const { t } = useTranslation(['feedback', 'common']);
  const { statistics, loading, error, fetchVoteStatistics, clearStatistics, clearError } = useFeedbackVoteStatistics();

  // Fetch statistics when dialog opens
  useEffect(() => {
    if (open && feedbackId) {
      fetchVoteStatistics(feedbackId);
    }

    return () => {
      if (!open) {
        clearStatistics();
        clearError();
      }
    };
  }, [open, feedbackId, fetchVoteStatistics, clearStatistics, clearError]);

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const maxVotes = statistics?.department_distribution?.length > 0
    ? Math.max(...statistics.department_distribution.map(d => d.vote_count))
    : 1;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={loading}
      PaperProps={{
        sx: {
          borderRadius: 2,
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <BarChart color="primary" />
            <Box>
              <Typography variant="h6">
                {t('feedback:vote.statistics_title', 'Vote Statistics')}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {feedbackTitle || statistics?.feedback_title}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            disabled={loading}
            edge="end"
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2, borderRadius: 2 }}
            onClose={clearError}
          >
            {error}
          </Alert>
        )}

        {!loading && !error && statistics && (
          <Box>
            {/* Summary Stats */}
            <Box display="flex" gap={2} mb={3}>
              <Box
                flex={1}
                p={2}
                borderRadius={2}
                bgcolor="primary.50"
                border={1}
                borderColor="primary.200"
              >
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <People color="primary" fontSize="small" />
                  <Typography variant="caption" color="primary">
                    {t('feedback:vote.total_votes', 'Total Votes')}
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold" color="primary">
                  {statistics.total_votes}
                </Typography>
              </Box>

              <Box
                flex={1}
                p={2}
                borderRadius={2}
                bgcolor="success.50"
                border={1}
                borderColor="success.200"
              >
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Business color="success" fontSize="small" />
                  <Typography variant="caption" color="success.main">
                    {t('feedback:vote.unique_departments', 'Departments')}
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  {statistics.unique_departments}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Department Distribution */}
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                {t('feedback:vote.department_distribution', 'Vote Distribution by Department')}
              </Typography>

              {statistics.department_distribution?.length > 0 ? (
                <List sx={{ mt: 2 }}>
                  {statistics.department_distribution.map((dept, index) => {
                    const percentage = statistics.total_votes > 0 
                      ? (dept.vote_count / statistics.total_votes) * 100 
                      : 0;

                    return (
                      <ListItem
                        key={dept.department_id}
                        sx={{
                          flexDirection: 'column',
                          alignItems: 'stretch',
                          mb: 2,
                          p: 2,
                          bgcolor: 'action.hover',
                          borderRadius: 2,
                        }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Chip
                              label={`#${index + 1}`}
                              size="small"
                              color="secondary"
                              variant="outlined"
                            />
                            <Typography variant="body2" fontWeight="medium">
                              {dept.department_name}
                            </Typography>
                          </Box>
                          <Typography variant="body2" fontWeight="bold">
                            {dept.vote_count} {dept.vote_count === 1 ? t('feedback:vote.vote_singular', 'vote') : t('feedback:vote.vote_plural', 'votes')}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={2}>
                          <LinearProgress
                            variant="determinate"
                            value={percentage}
                            sx={{
                              flex: 1,
                              height: 8,
                              borderRadius: 4,
                            }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 45 }}>
                            {percentage.toFixed(1)}%
                          </Typography>
                        </Box>
                      </ListItem>
                    );
                  })}
                </List>
              ) : (
                <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                  {t('feedback:vote.no_votes_yet', 'No votes yet')}
                </Alert>
              )}
            </Box>

            {/* Insights */}
            {statistics.unique_departments > 1 && (
              <Alert severity="success" sx={{ mt: 3, borderRadius: 2 }}>
                <Typography variant="body2">
                  <strong>{t('feedback:vote.cross_department_interest', 'Cross-department interest!')}</strong>
                  {' '}
                  {t('feedback:vote.multiple_departments_voted', 'This feedback has support from {{count}} different departments, indicating broader organizational interest.', {
                    count: statistics.unique_departments
                  })}
                </Typography>
              </Alert>
            )}

            {statistics.unique_departments === 1 && statistics.total_votes > 0 && (
              <Alert severity="info" sx={{ mt: 3, borderRadius: 2 }}>
                <Typography variant="body2">
                  {t('feedback:vote.single_department', 'All votes are from a single department, suggesting this is a department-specific need.')}
                </Typography>
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          color="inherit"
        >
          {t('common:buttons.close', 'Close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}